// ─── Auth (Core) ──────────────────────────────────────────────────
// Socle d'authentification COMMUN à toutes les apps de la suite (base de comptes
// unique). Version minimale e-mail/mot de passe (le Google Sign-In natif de
// MoPCal viendra plus tard, en option injectable). Enrichit la session avec le
// profil Firestore `users/{uid}` (langue, prénom…).
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import {
  onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword,
  sendEmailVerification, signOut, getAdditionalUserInfo,
} from 'firebase/auth';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { assurerProfilPublic } from '../services/profilsPublics';
import { creerEspace } from '../espace/espaces';
import { configurerAchatsNatifs, identifierUtilisateurNatif, deconnecterUtilisateurNatif } from '../forfaits/achatNatifService';
import { connexionAvecGoogle, connexionAvecApple } from './connexionTierceService';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext) || { user: null, loading: true };

// `apresInscription` (22/08) : callback APP-SPÉCIFIQUE optionnel, appelé une
// fois l'espace par défaut créé — {uid, espaceId}. Sert par ex. à My Calendar
// pour créer AUSSI un premier calendrier par défaut. Le Core reste
// app-agnostique : il ne connaît que la notion d'espace, pas de calendrier.
export function AuthProvider({ children, apresInscription }) {
  const [fbUser, setFbUser]   = useState(null);   // utilisateur Firebase brut
  const [profil, setProfil]   = useState(null);   // doc users/{uid}
  const [loading, setLoading] = useState(true);
  // CUSTOM CLAIMS du jeton (admin / commerce / ivv) — MÊME source que les
  // règles Firestore. Posés côté serveur (Cloud Function `appliquerClaims`) :
  // le profil Firestore ne sert qu'à l'affichage, jamais à autoriser.
  const [claims, setClaims] = useState({});

  // Session Firebase (persistée : AsyncStorage natif / localStorage web).
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => { setFbUser(u); setLoading(false); });
    return () => unsub();
  }, []);

  // Identité RevenueCat (achats natifs iOS/Android) : sans effet si le module
  // est absent (web, binaire pas encore migré) — voir `achatNatifService.js`.
  useEffect(() => {
    configurerAchatsNatifs();
    if (fbUser?.uid) identifierUtilisateurNatif(fbUser.uid);
    else deconnecterUtilisateurNatif();
  }, [fbUser?.uid]);

  // Profil Firestore (langue, prénom…), créé au 1er login s'il manque.
  useEffect(() => {
    if (!fbUser?.uid) { setProfil(null); return; }
    const ref = doc(db, 'users', fbUser.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) setProfil({ id: snap.id, ...snap.data() });
      else { setProfil({ id: fbUser.uid }); setDoc(ref, { email: fbUser.email || '', creeLe: new Date().toISOString() }, { merge: true }).catch(() => {}); }
    }, () => setProfil({ id: fbUser.uid }));
    return () => unsub();
  }, [fbUser?.uid]);

  // Claims du jeton, relus à chaque session. `true` force le rafraîchissement :
  // après un changement de rôle, les droits arrivent sans reconnexion.
  useEffect(() => {
    let vivant = true;
    if (!fbUser) { setClaims({}); return undefined; }
    fbUser.getIdTokenResult(true)
      .then(r => { if (vivant) setClaims(r?.claims || {}); })
      .catch(() => { if (vivant) setClaims({}); });
    return () => { vivant = false; };
  }, [fbUser?.uid]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Relecture manuelle du jeton (ex. après s'être accordé un rôle).
  const rafraichirRoles = useCallback(async () => {
    if (!fbUser) return {};
    try { const r = await fbUser.getIdTokenResult(true); setClaims(r?.claims || {}); return r?.claims || {}; }
    catch { return {}; }
  }, [fbUser]);

  // Dernière connexion (23/08, Cédric — vu dans MoP Admin, Fiche compte) :
  // écrite par le client lui-même à CHAQUE démarrage de session — la seule
  // source dispo côté client (l'horodatage Firebase Auth `lastSignInTime`
  // n'est lisible que côté Admin SDK, pas exposé aux apps).
  useEffect(() => {
    if (!fbUser?.uid) return;
    setDoc(doc(db, 'users', fbUser.uid), { derniereConnexionLe: new Date().toISOString() }, { merge: true }).catch(() => {});
  }, [fbUser?.uid]);

  // Registre public des noms : sans cette écriture, les AUTRES ne peuvent lire
  // mon prénom nulle part (`users/{uid}` m'est réservé) et n'affichent que mon
  // uid. Idempotent, donc silencieux tant que rien ne change.
  useEffect(() => {
    if (!fbUser?.uid || !profil) return;
    assurerProfilPublic(fbUser.uid, {
      prenom: profil.prenom || profil.nom || '',
      avatarCouleur: profil.avatarCouleur || null,
    });
  }, [fbUser?.uid, profil?.prenom, profil?.nom, profil?.avatarCouleur]);   // eslint-disable-line react-hooks/exhaustive-deps

  // `emailVerifieBump` (23/08) : `.reload()` mute `fbUser` EN PLACE (même
  // référence d'objet) — sans ce compteur, le useMemo ci-dessous ne
  // recalculerait jamais après une vérification d'email fraîche.
  const [emailVerifieBump, setEmailVerifieBump] = useState(0);
  const user = useMemo(() => {
    if (!fbUser) return null;
    return {
      uid: fbUser.uid,
      email: fbUser.email || '',
      emailVerifie: !!fbUser.emailVerified,
      // Google/Apple garantissent déjà l'email (vérifié par eux) — seul un
      // compte email/mot de passe peut être bloqué en attente de vérification.
      estCompteMotDePasse: (fbUser.providerData || []).some(p => p.providerId === 'password'),
      ...(profil || {}),
    };
  }, [fbUser, profil, emailVerifieBump]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-provisioning (22/08, étendu 23/08 à Google/Apple) : un compte tout
  // neuf sans espace atterrit sur un écran vide (« Aucun espace possédé »,
  // « Aucun calendrier accessible ») — mauvaise première impression. Le nom
  // n'a aucun effet fonctionnel tant qu'un seul espace par compte est
  // possible, donc pas d'écran à faire remplir : juste un espace par défaut,
  // silencieusement, commun aux trois façons de créer un compte.
  const provisionnerNouveauCompte = useCallback(async (uid, email) => {
    try {
      const espaceId = await creerEspace({ uid, emailCreateur: email || '' });
      await apresInscription?.({ uid, espaceId });
    } catch { /* non bloquant : l'utilisateur peut toujours créer son espace à la main */ }
  }, [apresInscription]);

  const connexion   = useCallback((email, mdp) => signInWithEmailAndPassword(auth, email.trim(), mdp), []);
  const inscription = useCallback(async (email, mdp) => {
    const cred = await createUserWithEmailAndPassword(auth, email.trim(), mdp);
    try { await sendEmailVerification(cred.user); } catch {}
    await provisionnerNouveauCompte(cred.user.uid, email);
    return cred;
  }, [provisionnerNouveauCompte]);
  // Google/Apple : un seul aller-retour fait à la fois connexion ET inscription
  // (Firebase crée le compte lui-même si l'identité n'existe pas encore) —
  // `getAdditionalUserInfo(...).isNewUser` distingue les deux cas.
  const connexionGoogle = useCallback(async () => {
    const cred = await connexionAvecGoogle();
    if (getAdditionalUserInfo(cred)?.isNewUser) await provisionnerNouveauCompte(cred.user.uid, cred.user.email);
    return cred;
  }, [provisionnerNouveauCompte]);
  const connexionApple = useCallback(async () => {
    const cred = await connexionAvecApple();
    if (getAdditionalUserInfo(cred)?.isNewUser) await provisionnerNouveauCompte(cred.user.uid, cred.user.email);
    return cred;
  }, [provisionnerNouveauCompte]);
  const deconnexion = useCallback(() => signOut(auth), []);
  const renvoyerVerification = useCallback(() => (auth.currentUser ? sendEmailVerification(auth.currentUser) : Promise.resolve()), []);

  const value = useMemo(() => ({
    user, loading, claims, rafraichirRoles,
    // Rôles : le CLAIM du jeton fait foi (même source que les règles
    // Firestore) ; le profil Firestore ne sert que de repli d'affichage.
    estIVV:      claims.ivv === true      || !!user?.ivv,
    estAdmin:    claims.admin === true    || !!user?.admin || user?.role === 'admin',
    estCommerce: claims.commerce === true || !!user?.commerce || user?.role === 'commerce',
    connexion, inscription, deconnexion, renvoyerVerification,
  }), [user, loading, claims, rafraichirRoles, connexion, inscription, deconnexion, renvoyerVerification]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
